import React, { useState, useEffect, useRef } from "react";
import CircularLoading from "../components/CircularLoading";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import ConfirmationDialogRaw from "../components/ConfirmationDialogRaw";
import { downloadCsv } from "../common/sharedFunctions";
import MaterialTable from "material-table";
import {
  Grid,
  Typography,
  Modal,
  Button,
  FormControl,
  FormLabel,
  Select,
  MenuItem,
  TextField,
  Chip,
  Tooltip,
} from "@mui/material";
import { api } from "common";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import UsersCombo from "../components/UsersCombo";
import { makeStyles } from "@mui/styles";
import { useTranslation } from "react-i18next";
import CancelIcon from "@mui/icons-material/Cancel";
import PaymentIcon from "@mui/icons-material/Payment";
import PlaylistAddCheckIcon from "@mui/icons-material/PlaylistAddCheck";
import AlertDialog from "../components/AlertDialog";
import { colors } from "../components/Theme/WebTheme";
import Card from "../components/Card/Card";
import CardBody from "../components/Card/CardBody";
import {
  BidModal,
  acceptBid,
} from "../common/sharedFunctions";
import BookingHistoryOld from "./BookingHistory-old";

const useStyles = makeStyles((theme) => ({
  modal: {
    display: "flex",
    padding: theme.spacing(1),
    alignItems: "center",
    justifyContent: "center",
  },
  paper: {
    width: 680,
    backgroundColor: theme.palette.background.paper,
    border: "2px solid #000",
    boxShadow: theme.shadows[5],
    padding: theme.spacing(2, 4, 3),
  },
  
  filterCard: {
    // Removed margin styles as they're now handled inline
    marginTop: theme.spacing(2),
  },
  filterTitle: {
    fontSize: "1.2rem",
    fontWeight: "bold",
    marginBottom: theme.spacing(2),
    textAlign: "left",
  },
  filterContent: {
    display: "flex",
    alignItems: "flex-end",
    gap: theme.spacing(2),
    justifyContent: "space-between",
    width: "100%",
  },
  searchField: {
    flex: 1,
    minWidth: 0,
    marginRight: theme.spacing(2),
  },
  exportButton: {
    height: "40px",
    minWidth: "150px",
    borderRadius: "8px",
    backgroundColor: "#28a745",
    "&:hover": {
      backgroundColor: "#218838",
    },
    flexShrink: 0,
  },
  tableCard: {
    marginTop: theme.spacing(2),
  },
  infoButton: {
    minWidth: "40px",
    height: "32px",
    borderRadius: "8px",
    marginRight: theme.spacing(1),
    padding: "4px 8px",
    border: `1px solid ${colors.MAIN_COLOR}`,
    color: colors.MAIN_COLOR,
    backgroundColor: "transparent",
    "&:hover": {
      backgroundColor: colors.MAIN_COLOR,
      color: "white",
    },
    "&:disabled": {
      border: "1px solid #ccc",
      color: "#ccc",
      backgroundColor: "#f5f5f5",
      cursor: "not-allowed",
      "&:hover": {
        backgroundColor: "#f5f5f5",
        color: "#ccc",
      },
    },
  },
  cancelButton: {
    height: "32px",
    borderRadius: "8px",
    padding: "4px 12px",
    border: `1px solid ${colors.MAIN_COLOR}`,
    color: colors.MAIN_COLOR,
    backgroundColor: "transparent",
    "&:hover": {
      backgroundColor: colors.MAIN_COLOR,
      color: "white",
    },
    "&:disabled": {
      border: "1px solid #ccc",
      color: "#ccc",
      backgroundColor: "#f5f5f5",
      cursor: "not-allowed",
      "&:hover": {
        backgroundColor: "#f5f5f5",
        color: "#ccc",
      },
    },
  },
  statusChip: {
    borderRadius: "20px",
    fontWeight: "bold",
    fontSize: "0.75rem",
  },
}));

const icons = {
  paypal: require("../assets/payment-icons/paypal-logo.png").default,
  stripe: require("../assets/payment-icons/stripe-logo.png").default,
};

const BookingHistory = (props) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();
  const { cancelBooking, updateBooking, RequestPushMsg, forceEndBooking } = api;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const auth = useSelector((state) => state.auth);
  const userdata = useSelector((state) => state.usersdata);
  const settings = useSelector((state) => state.settingsdata.settings);
  const role = useSelector((state) => state.auth.profile.usertype);
  const [paymentModalStatus, setPaymentModalStatus] = useState(false);
  const providers = useSelector((state) => state.paymentmethods.providers);
  const [selectedProvider, setSelectedProvider] = useState();
  const [selectedProviderIndex, setSelectedProviderIndex] = useState(0);
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [openConfirm, setOpenConfirm] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState();
  const bookinglistdata = useSelector((state) => state.bookinglistdata);
  const [users, setUsers] = useState(null);
  const [userCombo, setUserCombo] = useState(null);
  const rootRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [rowIndex, setRowIndex] = useState();
  const [bidModalStatus, setBidModalStatus] = useState();
  const [selectedBidder, setSelectedBidder] = useState({ value: "" });
  const [commonAlert, setCommonAlert] = useState({ open: false, msg: "" });
  const classes = useStyles();
  const [walletModalStatus, setWalletModalStatus] = useState(false);

  // Define new column structure for redesigned table
  const columns = [
    { 
      title: t('booking_ref'), 
      field: 'reference', 
      cellStyle: { textAlign: isRTL === 'rtl' ? 'right' : 'left' }
    },
    { 
      title: t('booking_date'), 
      field: 'bookingDate', 
      render: rowData => rowData.bookingDate ? new Date(rowData.bookingDate).toLocaleDateString() + " " + new Date(rowData.bookingDate).toLocaleTimeString() : null,
      cellStyle: { textAlign: isRTL === 'rtl' ? 'right' : 'left' }
    },
    { 
      title: t('car_type'), 
      field: 'carType',
      cellStyle: { textAlign: isRTL === 'rtl' ? 'right' : 'left' }
    },
    { 
      title: t('assign_driver'), 
      field: 'driver_name',
      render: rowData => rowData.driver_name || '-',
      cellStyle: { textAlign: isRTL === 'rtl' ? 'right' : 'left' }
    },
    { 
      title: t('user_name'), 
      field: 'customer_name',
      render: rowData => rowData.customer_name || '-',
      cellStyle: { textAlign: isRTL === 'rtl' ? 'right' : 'left' }
    },
    { 
      title: t('origin'), 
      field: 'pickupAddress',
      render: rowData => {
        const fullAddress = rowData.pickupAddress || rowData.pickup?.add || '-';
        if (fullAddress === '-') return '-';
        
        const shortAddress = fullAddress.length > 8 ? fullAddress.substring(0, 8) + '...' : fullAddress;
        
        return (
          <Tooltip 
            title={fullAddress} 
            arrow 
            placement="top"
            enterDelay={300}
            leaveDelay={200}
          >
            <span 
              style={{ 
                cursor: 'help',
                textDecoration: fullAddress.length > 8 ? 'underline dotted' : 'none',
                textDecorationColor: '#ccc',
                display: 'inline-block',
                maxWidth: '100%'
              }}
            >
              {shortAddress}
            </span>
          </Tooltip>
        );
      },
      cellStyle: { textAlign: isRTL === 'rtl' ? 'right' : 'left' }
    },
    { 
      title: t('booking_status'), 
      field: 'status', 
      render: rowData => {
        const getStatusColor = (status) => {
          switch (status?.toUpperCase()) {
            case 'COMPLETE':
            case 'COMPLETED':
            case 'COMPLETADO':
              return { backgroundColor: '#d4edda', color: '#155724' };
            case 'NEW':
            case 'NUEVO':
              return { backgroundColor: '#d1ecf1', color: '#0c5460' };
            case 'ACCEPTED':
            case 'ACEPTADO':
              return { backgroundColor: '#cce5ff', color: '#004085' };
            case 'STARTED':
            case 'INICIADO':
              return { backgroundColor: '#fff3cd', color: '#856404' };
            case 'REACHED':
            case 'LLEGADO':
              return { backgroundColor: '#ffeaa7', color: '#b8860b' };
            case 'PENDING':
            case 'PENDIENTE':
              return { backgroundColor: '#fff3cd', color: '#856404' };
            case 'PAYMENT_PENDING':
            case 'PAGO_PENDIENTE':
              return { backgroundColor: '#fff3cd', color: '#856404' };
            case 'PAID':
            case 'PAGADO':
              return { backgroundColor: '#e2e3f1', color: '#6f42c1' };
            case 'CANCELLED':
            case 'CANCELADO':
              return { backgroundColor: '#f8d7da', color: '#721c24' };
            default:
              return { backgroundColor: '#e2e3e5', color: '#383d41' };
          }
        };
        
        return (
          <Chip
            label={t(rowData.status)}
            className={classes.statusChip}
            style={getStatusColor(rowData.status)}
          />
        );
      },
      cellStyle: { textAlign: isRTL === 'rtl' ? 'right' : 'left' }
    },
    { 
      title: t('otp'), 
      field: 'otp',
      cellStyle: { textAlign: isRTL === 'rtl' ? 'right' : 'left' }
    },
    { 
      title: t('trip_cost'), 
      field: 'trip_cost',
      render: rowData => rowData.trip_cost ? 
        (auth.profile && auth.profile.swipe_symbol === false
          ? (auth.profile.symbol || '$') + rowData.trip_cost
          : rowData.trip_cost + (auth.profile.symbol || '$'))
        : '-',
      cellStyle: { textAlign: isRTL === 'rtl' ? 'right' : 'left' }
    }
  ];

  // for rebid mod
  const [bidPrice, setBidPrice] = useState(null);
  // for rebid mod

  useEffect(() => {
    if (bookinglistdata.bookings) {
      setData(bookinglistdata.bookings);
      setFilteredData(bookinglistdata.bookings);
    } else {
      setData([]);
      setFilteredData([]);
    }
  }, [bookinglistdata.bookings]);

  // Search functionality
  useEffect(() => {
    if (!searchTerm) {
      setFilteredData(data);
    } else {
      const filtered = data.filter(booking => 
        booking.reference?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        booking.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        booking.driver_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        booking.status?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        booking.carType?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredData(filtered);
    }
  }, [data, searchTerm]);

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleExportCSV = () => {
    const headerRow = columns.map(col => col.title);
    const dataRows = filteredData.map(row => {
      return columns.map(col => {
        if (col.field === 'bookingDate') {
          return row.bookingDate ? new Date(row.bookingDate).toLocaleDateString() + " " + new Date(row.bookingDate).toLocaleTimeString() : '';
        }
        return row[col.field] || '';
      });
    });
    
    const csvContent = [headerRow, ...dataRows]
      .map(e => e.join(","))
      .join("\n");
    const csvFileName = "booking_history.csv";
    downloadCsv(csvContent, csvFileName);
  };

  const handleTripDetailsOpen = (booking) => {
    navigate('/trip-details', { state: { tripData: booking } });
  };

  useEffect(() => {
    if (userdata.users) {
      let arr = [];
      for (let i = 0; i < userdata.users.length; i++) {
        let user = userdata.users[i];
        if (
          user.usertype === "driver" &&
          ((user.fleetadmin === auth.profile.uid && role === "fleetadmin") ||
            role === "admin")
        ) {
          arr.push({
            firstName: user.firstName,
            lastName: user.lastName,
            mobile: user.mobile,
            email: user.email,
            uid: user.id,
            desc:
              user.firstName +
              " " +
              user.lastName +
              " (" +
              (settings.AllowCriticalEditsAdmin ? user.mobile : "Hidden") +
              ") " +
              (settings.AllowCriticalEditsAdmin ? user.email : "Hidden"),
            pushToken: user.pushToken ? user.pushToken : "",
            carType: user.carType,
          });
        }
      }
      setUsers(arr);
    }
  }, [
    userdata.users,
    settings.AllowCriticalEditsAdmin,
    auth.profile.uid,
    role,
  ]);

  const assignDriver = () => {
    let booking = data[rowIndex];
    if (booking["requestedDrivers"]) {
      booking["requestedDrivers"][userCombo.uid] = true;
    } else {
      booking["requestedDrivers"] = {};
      booking["requestedDrivers"][userCombo.uid] = true;
    }
    dispatch(updateBooking(booking));
    if (userCombo.pushToken) {
      RequestPushMsg(userCombo.pushToken, {
        title: t("notification_title"),
        msg: t("new_booking_notification"),
        screen: "DriverTrips",
        channelId: settings.CarHornRepeat ? "bookings-repeat" : "bookings",
      });
    }
    setUserCombo(null);
    handleClose();
    alert("Driver assigned successfully and notified.");
  };

  const onConfirmClose = (value) => {
    if (value) {
      dispatch(
        cancelBooking({
          reason: value,
          booking: selectedBooking,
          cancelledBy: role,
        })
      );
    }
    setOpenConfirm(false);
  };

  const handleChange = (e) => {
    if (e.target.name === "selectedProviderIndex") {
      setSelectedProviderIndex(parseInt(e.target.value));
      setSelectedProvider(providers[parseInt(e.target.value)]);
    }
    if (e.target.name === "selectedBidder") {
      setSelectedBidder({ value: e.target.value });
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handlePaymentModalClose = (e) => {
    setTimeout(() => {
      setPaymentModalStatus(false);
    }, 1500);
  };

  const handleWalletModalClose = (e) => {
    setTimeout(() => {
      setWalletModalStatus(false);
    }, 1500);
  };

  useEffect(() => {
    if (providers) {
      setSelectedProvider(providers[0]);
    }
  }, [providers]);

  const processPayment = (rowData) => {
    const curBooking = rowData;

    if (curBooking.payment_mode === "card") {
      let paymentCost = curBooking.selectedBid
        ? curBooking.selectedBid.trip_cost
        : curBooking.trip_cost;
      const paymentPacket = {
        payment_mode: "card",
        customer_paid: parseFloat(paymentCost).toFixed(settings.decimal),
        cardPaymentAmount: parseFloat(paymentCost).toFixed(settings.decimal),
        discount: 0,
        usedWalletMoney: 0,
        cashPaymentAmount: 0,
        promo_applied: false,
        promo_details: null,
        payableAmount: parseFloat(paymentCost).toFixed(settings.decimal),
      };
      curBooking.paymentPacket = paymentPacket;
      dispatch(updateBooking(curBooking));
      setSelectedBooking(curBooking);
      setPaymentModalStatus(true);
    }
    if (curBooking.payment_mode === "wallet") {
      let curBooking = { ...rowData };
      if (rowData.status === "PAYMENT_PENDING") {
        curBooking.status = "NEW";
        curBooking.prepaid = true;
      } else if (rowData.status === "PENDING") {
        curBooking.status = "PAID";
      } else if (rowData.status === "NEW") {
        curBooking.prepaid = true;
        curBooking.status = "ACCEPTED";
      }

      curBooking.customer_paid =
        curBooking.status === "ACCEPTED"
          ? 0
          : parseFloat(curBooking.trip_cost).toFixed(settings.decimal);
      curBooking.discount = 0;
      curBooking.usedWalletMoney =
        curBooking.status === "ACCEPTED"
          ? 0
          : parseFloat(curBooking.trip_cost).toFixed(settings.decimal);
      curBooking.cardPaymentAmount = 0;
      curBooking.cashPaymentAmount = 0;
      curBooking.payableAmount =
        curBooking.status === "ACCEPTED"
          ? parseFloat(curBooking.selectedBid.trip_cost).toFixed(
              settings.decimal
            )
          : parseFloat(curBooking.trip_cost).toFixed(settings.decimal);

      if (curBooking.status === "ACCEPTED") {
        curBooking.driver = curBooking.selectedBid.driver;
        curBooking.driver_image = curBooking.selectedBid.driver_image;
        curBooking.driver_name = curBooking.selectedBid.driver_name;
        curBooking.driver_contact = curBooking.selectedBid.driver_contact;
        curBooking.driver_token = curBooking.selectedBid.driver_token;
        curBooking.vehicle_number = curBooking.selectedBid.vehicle_number;
        curBooking.driverRating = curBooking.selectedBid.driverRating;
        curBooking.trip_cost = curBooking.selectedBid.trip_cost;
        curBooking.convenience_fees = curBooking.selectedBid.convenience_fees;
        curBooking.driver_share = curBooking.selectedBid.driver_share;
        curBooking.fleetadmin = curBooking.selectedBid.fleetadmin
          ? curBooking.selectedBid.fleetadmin
          : null;
        curBooking.fleetCommission = curBooking.selectedBid.fleetadmin
          ? (
              (parseFloat(curBooking.selectedBid.trip_cost) -
                parseFloat(curBooking.selectedBid.convenience_fees)) *
              (parseFloat(curBooking.fleet_admin_comission) / 100)
            ).toFixed(2)
          : null;
        curBooking.driverOffers = {};
        curBooking.requestedDrivers = {};
        curBooking.driverEstimates = {};
        curBooking.selectedBid = {};
      }
      dispatch(updateBooking(curBooking));

      setBidModalStatus(false);
    }
    if (
      curBooking.payment_mode === "cash" ||
      curBooking.payment_mode === "yappi"
    ) {
      let curBooking = { ...rowData };
      if (rowData.status === "PAYMENT_PENDING") {
        curBooking.status = "NEW";
      } else if (rowData.status === "PENDING") {
        curBooking.status = "PAID";
      } else if (rowData.status === "NEW") {
        curBooking.status = "ACCEPTED";
      }

      curBooking.customer_paid =
        curBooking.status === "ACCEPTED"
          ? 0
          : parseFloat(curBooking.trip_cost).toFixed(settings.decimal);
      curBooking.discount = 0;
      curBooking.usedWalletMoney = 0;
      curBooking.cardPaymentAmount = 0;
      curBooking.cashPaymentAmount =
        curBooking.status === "ACCEPTED"
          ? 0
          : parseFloat(curBooking.trip_cost).toFixed(settings.decimal);
      curBooking.payableAmount =
        curBooking.status === "ACCEPTED"
          ? parseFloat(curBooking.selectedBid.trip_cost).toFixed(
              settings.decimal
            )
          : parseFloat(curBooking.trip_cost).toFixed(settings.decimal);

      if (curBooking.status === "ACCEPTED") {
        curBooking.driver = curBooking.selectedBid.driver;
        curBooking.driver_image = curBooking.selectedBid.driver_image;
        curBooking.driver_name = curBooking.selectedBid.driver_name;
        curBooking.driver_contact = curBooking.selectedBid.driver_contact;
        curBooking.driver_token = curBooking.selectedBid.driver_token;
        curBooking.vehicle_number = curBooking.selectedBid.vehicle_number;
        curBooking.driverRating = curBooking.selectedBid.driverRating;
        curBooking.trip_cost = curBooking.selectedBid.trip_cost;
        curBooking.convenience_fees = curBooking.selectedBid.convenience_fees;
        curBooking.driver_share = curBooking.selectedBid.driver_share;
        curBooking.fleetadmin = curBooking.selectedBid.fleetadmin
          ? curBooking.selectedBid.fleetadmin
          : null;
        curBooking.fleetCommission = curBooking.selectedBid.fleetadmin
          ? (
              ((parseFloat(curBooking.selectedBid.trip_cost) -
                parseFloat(curBooking.selectedBid.convenience_fees)) *
                parseFloat(curBooking.fleet_admin_comission)) /
              100
            ).toFixed(2)
          : null;
        curBooking.driverOffers = {};
        curBooking.requestedDrivers = {};
        curBooking.driverEstimates = {};
        curBooking.selectedBid = {};
      }
      dispatch(updateBooking(curBooking));
      setBidModalStatus(false);
    }
  };

  const handleWalletPayment = (e) => {
    e.preventDefault();
    let curBooking = { ...selectedBooking };
    let paymentCost = curBooking.selectedBid
      ? curBooking.selectedBid.trip_cost
      : curBooking.trip_cost;
    if (parseFloat(paymentCost) > parseFloat(auth.profile.walletBalance)) {
      setCommonAlert({ open: true, msg: t("wallet_balance_low") });
    } else {
      curBooking.prepaid =
        curBooking.status === "NEW" || curBooking.status === "PAYMENT_PENDING"
          ? true
          : false;
      curBooking.status = curBooking.status === "NEW" ? "ACCEPTED" : "PAID";
      curBooking.customer_paid = parseFloat(paymentCost).toFixed(
        settings.decimal
      );
      curBooking.discount = 0;
      curBooking.usedWalletMoney = parseFloat(paymentCost).toFixed(
        settings.decimal
      );
      curBooking.cardPaymentAmount = 0;
      curBooking.cashPaymentAmount = 0;
      curBooking.payableAmount = 0;
      curBooking.promo_applied = false;
      curBooking.promo_details = null;
      curBooking.paymentPacket = null;
      curBooking.driver = curBooking.selectedBid
        ? curBooking.selectedBid.driver
        : curBooking.driver;
      curBooking.driver_image = curBooking.selectedBid
        ? curBooking.selectedBid.driver_image
        : curBooking.driver_image;
      curBooking.driver_name = curBooking.selectedBid
        ? curBooking.selectedBid.driver_name
        : curBooking.driver_name;
      curBooking.driver_contact = curBooking.selectedBid
        ? curBooking.selectedBid.driver_contact
        : curBooking.driver_contact;
      curBooking.driver_token = curBooking.selectedBid
        ? curBooking.selectedBid.driver_token
        : curBooking.driver_token;
      curBooking.vehicle_number = curBooking.selectedBid
        ? curBooking.selectedBid.vehicle_number
        : curBooking.vehicle_number;
      curBooking.driverRating = curBooking.selectedBid
        ? curBooking.selectedBid.driverRating
        : curBooking.driverRating;
      curBooking.trip_cost = curBooking.selectedBid
        ? curBooking.selectedBid.trip_cost
        : curBooking.trip_cost;
      curBooking.convenience_fees = curBooking.selectedBid
        ? curBooking.selectedBid.convenience_fees
        : curBooking.convenience_fees;
      curBooking.driver_share = curBooking.selectedBid
        ? curBooking.selectedBid.driver_share
        : curBooking.driver_share;
      curBooking.driverOffers = null;
      curBooking.requestedDrivers = null;
      curBooking.driverEstimates = null;
      curBooking.selectedBid = null;

      dispatch(updateBooking(curBooking));

      setTimeout(() => {
        setWalletModalStatus(false);
      }, 1500);
    }
  };

  const handleBidModalClose = () => {
    setBidModalStatus(false);
  };

  const handleCommonAlertClose = (e) => {
    e.preventDefault();
    setCommonAlert({ open: false, msg: "" });
  };

  const [selectedRow, setSelectedRow] = useState(null);

  // Check if new booking history design is enabled
  const useNewDesign = settings?.new_booking_history_design === true;

  // If new design is disabled, use the old design
  if (!useNewDesign) {
    return <BookingHistoryOld {...props} />;
  }

  return bookinglistdata.loading ? (
    <CircularLoading />
  ) : (
    <div style={{ width: "100%", overflowX: "hidden" }}>
      {/* Filter Card */}
      <div style={{ padding: "0 24px", width: "100%", boxSizing: "border-box" }}>
        <Card className={classes.filterCard} style={{ width: "100%", margin: "24px 0 15px 0" }}>
          <CardBody style={{ padding: "24px" }}>
            <Typography className={classes.filterTitle}>
              {t("filter")}
            </Typography>
            <div className={classes.filterContent}>
              <div className={classes.searchField}>
                <Typography variant="body2" gutterBottom style={{ marginBottom: "8px", color: "#666" }}>
                  Buscar por referencia
                </Typography>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder={t("search")}
                  value={searchTerm}
                  onChange={handleSearchChange}
                  size="small"
                />
              </div>
              <Button
                className={classes.exportButton}
                variant="contained"
                startIcon={<FileDownloadIcon />}
                onClick={handleExportCSV}
              >
                {t("export")}
              </Button>
            </div>
          </CardBody>
        </Card>
      </div>

            {/* Data Table */}
      <div style={{ padding: "0 24px", width: "100%", boxSizing: "border-box" }}>
        <div style={{ width: "100%", overflowX: "auto" }}>
          <MaterialTable
          title={t("booking_title")}
          style={{
            direction: isRTL === "rtl" ? "rtl" : "ltr",
            borderRadius: "8px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            marginTop: "16px",
          }}
            columns={columns}
            data={filteredData}
            onRowClick={(evt, selectedRow) =>
              setSelectedRow(selectedRow.tableData.id)
            }
            options={{
              exportButton: false,
              search: false,
              maxColumnSort: "all_columns",
              actionsColumnIndex: -1,
              rowStyle: (rowData) => ({
                backgroundColor:
                  selectedRow === rowData.tableData.id
                    ? colors.THIRDCOLOR
                    : colors.WHITE,
              }),
              maxBodyHeight: "calc(100vh - 350px)",
              headerStyle: {
                position: "sticky",
                top: "0px",
                backgroundColor: colors.SECONDARY,
                color: "#fff",
                fontSize: "0.9em",
                fontWeight: "bold",
                fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                zIndex: 1,
              },
            }}
            localization={{
              header: {
                actions: t("actions"),
              },
              pagination: {
                labelDisplayedRows: "{from}-{to} " + t("of") + " {count}",
                firstTooltip: t("first_page_tooltip"),
                previousTooltip: t("previous_page_tooltip"),
                nextTooltip: t("next_page_tooltip"),
                lastTooltip: t("last_page_tooltip"),
              },
            }}
            actions={[
              (rowData) => ({
                icon: () => (
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <Button
                      className={classes.infoButton}
                      variant="outlined"
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleTripDetailsOpen(rowData);
                      }}
                    >
                      <InfoOutlinedIcon fontSize="small" />
                    </Button>
                    <Button
                      className={classes.cancelButton}
                      variant="outlined"
                      size="small"
                      disabled={
                        !(rowData.status === "NEW" ||
                          rowData.status === "ACCEPTED" ||
                          rowData.status === "PAYMENT_PENDING")
                      }
                      startIcon={<CancelIcon fontSize="small" />}
                    >
                      <Typography variant="caption">
                        {t("cancel_booking")}
                      </Typography>
                    </Button>
                  </div>
                ),
                disabled: false, // Info button should always be clickable
                onClick: (event, rowData) => {
                  if (
                    settings.AllowCriticalEditsAdmin &&
                    (role === "customer" ||
                      role === "admin" ||
                      role === "fleetadmin")
                  ) {
                    if (rowData.status === "NEW" || rowData.status === "ACCEPTED") {
                      setSelectedBooking(rowData);
                      setOpenConfirm(true);
                    } else {
                      setTimeout(() => {
                        dispatch(
                          cancelBooking({
                            reason: t("cancelled_incomplete_booking"),
                            booking: rowData,
                            cancelledBy: role,
                          })
                        );
                      }, 1500);
                    }
                  } else {
                    alert(t("demo_mode"));
                  }
                },
              }),
              (rowData) =>
                rowData.status === "NEW" &&
                role === "customer" &&
                rowData.driverOffers
                  ? {
                      icon: () => (
                        <Button
                          className={classes.cancelButton}
                          variant="outlined"
                          size="small"
                          startIcon={<PlaylistAddCheckIcon fontSize="small" />}
                        >
                          <Typography variant="caption">
                            {t("selectBid")}
                          </Typography>
                        </Button>
                      ),
                      onClick: (event, rowData) => {
                        setRowIndex(rowData.tableData.id);
                        setSelectedBooking(rowData);
                        setBidModalStatus(true);
                      },
                    }
                  : null,
              (rowData) =>
                rowData.status === "NEW" &&
                (role === "admin" || role === "fleetadmin") &&
                settings.autoDispatch === false &&
                rowData.category !== "bid"
                  ? {
                      icon: () => (
                        <Button
                          className={classes.cancelButton}
                          variant="outlined"
                          size="small"
                          startIcon={<PersonAddIcon fontSize="small" />}
                        >
                          <Typography variant="caption">
                            {t("assign_driver")}
                          </Typography>
                        </Button>
                      ),
                      onClick: (event, rowData) => {
                        setOpen(true);
                        setRowIndex(rowData.tableData.id);
                      },
                    }
                  : null,
              (rowData) =>
                (rowData.status === "PENDING" ||
                  rowData.status === "PAYMENT_PENDING") &&
                role === "customer"
                  ? {
                      icon: () => (
                        <Button
                          className={classes.cancelButton}
                          variant="outlined"
                          size="small"
                          startIcon={<PaymentIcon fontSize="small" />}
                        >
                          <Typography variant="caption">
                            {t("paynow_button")}
                          </Typography>
                        </Button>
                      ),
                      onClick: (event, rowData) => {
                        processPayment(rowData);
                      },
                    }
                  : null,
              (rowData) =>
                rowData.status === "STARTED" && role === "admin"
                  ? {
                      icon: () => (
                        <Button
                          className={classes.cancelButton}
                          variant="outlined"
                          size="small"
                          startIcon={<PlaylistAddCheckIcon fontSize="small" />}
                        >
                          <Typography variant="caption">
                            {t("force_end")}
                          </Typography>
                        </Button>
                      ),
                      onClick: (event, rowData) => {
                        dispatch(forceEndBooking(rowData));
                      },
                    }
                  : null,
                        ]}
          />
        </div>
      </div>
        {/* rebid code placed to add few params below*/}
      <BidModal
        ref={rootRef.current}
        role={role}
        selectedBooking={selectedBooking}
        bidModalStatus={bidModalStatus}
        handleBidModalClose={handleBidModalClose}
        classes={classes}
        settings={settings}
        bidPrice={bidPrice}
        setBidPrice={(e) => {
          setBidPrice(e.target.value);
        }}
        allowRebid={settings && settings.UseRebidMode}
        acceptBid={() => {
          const bookingObj = acceptBid(selectedBooking, selectedBidder);
          RequestPushMsg(bookingObj.selectedBid.driver_token, {
            title: t("notification_title"),
            msg: t("new_booking_notification"),
            screen: "BookedCab",
            params: { bookingId: bookingObj.id },
          });
          processPayment(bookingObj);
          handleBidModalClose();
        }}
        placeRebid={() => {
          let rebid = settings && settings.UseRebidMode ? true : false;
          if (!rebid) {
            alert("No REBID");
            return;
          }
          let amt = true;
          if (!bidPrice) {
            amt = false;
          }
          if (parseInt(bidPrice) <= 0) {
            amt = false;
          }

          if (!amt) {
            alert(t("enter_bid_amount"));
            return;
          }
          console.log("selectedBidder: ", selectedBidder);
          console.log("selectedBidder: ", selectedBidder.value);
          if (
            selectedBidder &&
            typeof selectedBidder.value !== "undefined" &&
            selectedBidder.value
          ) {
          } else {
            alert(t("select_bidder"));
            return;
          }
          let tempBooking = { ...selectedBooking };
          let selectedB =
            typeof tempBooking.driverOffers !== "undefined" &&
            tempBooking.driverOffers[selectedBidder.value] !== "undefined"
              ? tempBooking.driverOffers[selectedBidder.value]
              : null;
          if (
            typeof tempBooking.negotiations !== "undefined" &&
            tempBooking.negotiations[selectedBidder.value] !== "undefined"
          ) {
            delete tempBooking.negotiations[selectedBidder.value];
            console.log("Delete Driver Negotiation");
          }
          if (
            typeof tempBooking.driverOffers !== "undefined" &&
            tempBooking.driverOffers[selectedBidder.value] !== "undefined"
          ) {
            delete tempBooking.driverOffers[selectedBidder.value];
            console.log("Delete Driver Offer");
          }
          // tempBooking["bidPrice"] = bidPrice;

          if (typeof tempBooking.bidPriceData == "undefined") {
            tempBooking.bidPriceData = {};
            tempBooking.bidPriceData[selectedBidder.value] = bidPrice;
          } else if (
            typeof tempBooking.bidPriceData[selectedBidder.value] == "undefined"
          ) {
            tempBooking.bidPriceData[selectedBidder.value] = bidPrice;
          } else {
            tempBooking.bidPriceData[selectedBidder.value] = bidPrice;
          }

          dispatch(updateBooking(tempBooking));
          if (selectedB.driver_token) {
            RequestPushMsg(selectedB.driver_token, {
              title: t("notification_title"),
              msg: t("new_booking_notification"),
              screen: "BookedCab",
              params: { bookingId: tempBooking.id },
            });
          }
          setBidPrice(null);
          setSelectedBidder("");
          handleBidModalClose();
        }}
        selectedBidder={selectedBidder}
        handleChange={handleChange}
      />
      {/* rebid code for placing rebid from customer end */}
      {selectedBooking &&
      selectedBooking.payment_mode === "wallet" &&
      (selectedBooking.status === "PENDING" ||
        (selectedBooking &&
          selectedBooking.selectedBid &&
          selectedBooking.status === "NEW")) &&
      role === "customer" ? (
        <Modal
          disablePortal
          disableEnforceFocus
          disableAutoFocus
          open={walletModalStatus}
          onClose={handleWalletModalClose}
          className={classes.modal}
          container={() => rootRef.current}
        >
          <Grid container spacing={2} className={classes.paper}>
            <Grid
              item
              xs={12}
              sm={12}
              md={12}
              lg={12}
              style={{ marginBottom: "20px" }}
            >
              <Typography style={{ fontWeight: "bolder", marginBottom: 10 }}>
                {t("payment")}
              </Typography>
              {selectedBooking ? (
                <Typography color={"primary"} style={{ fontSize: 30 }}>
                  {auth.profile.swipe_symbol === false
                    ? auth.profile.symbol + selectedBooking.trip_cost
                    : selectedBooking.trip_cost + auth.profile.symbol}
                </Typography>
              ) : null}
              <Typography>
                {t("use_wallet_balance") +
                  " " +
                  (auth.profile.swipe_symbol === false
                    ? auth.profile.symbol + auth.profile.walletBalance
                    : auth.profile.walletBalance + auth.profile.symbol) +
                  ")"}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={12} md={12} lg={12}>
              <Button
                onClick={handleWalletModalClose}
                variant="contained"
                color="primary"
              >
                {t("cancel")}
              </Button>
              <Button
                variant="contained"
                color="primary"
                style={{ marginLeft: 10 }}
                onClick={handleWalletPayment}
              >
                {t("paynow_button")}
              </Button>
            </Grid>
          </Grid>
        </Modal>
      ) : null}
      {selectedBooking &&
      selectedBooking.paymentPacket &&
      selectedBooking.payment_mode === "card" &&
      (selectedBooking.status === "PENDING" ||
        selectedBooking.status === "PAYMENT_PENDING" ||
        (selectedBooking &&
          selectedBooking.selectedBid &&
          selectedBooking.status === "NEW")) &&
      role === "customer" ? (
        <Modal
          disablePortal
          disableEnforceFocus
          disableAutoFocus
          open={paymentModalStatus}
          onClose={handlePaymentModalClose}
          className={classes.modal}
          container={() => rootRef.current}
        >
          <Grid container spacing={2} className={classes.paper}>
            {providers && selectedProvider && selectedBooking ? (
              <form action={selectedProvider.link} method="POST">
                <input
                  type="hidden"
                  name="order_id"
                  value={selectedBooking.id}
                />
                <input
                  type="hidden"
                  name="amount"
                  value={selectedBooking.trip_cost}
                />
                <input
                  type="hidden"
                  name="currency"
                  value={selectedBooking.currency}
                />
                <input
                  type="hidden"
                  name="product_name"
                  value={t("bookingPayment")}
                />
                <input
                  type="hidden"
                  name="first_name"
                  value={auth.profile.firstName}
                />
                <input
                  type="hidden"
                  name="last_name"
                  value={auth.profile.lastName}
                />
                <input type="hidden" name="quantity" value={1} />
                <input
                  type="hidden"
                  name="cust_id"
                  value={selectedBooking.customer}
                />
                <input
                  type="hidden"
                  name="mobile_no"
                  value={selectedBooking.customer_contact}
                />
                <input
                  type="hidden"
                  name="email"
                  value={selectedBooking.customer_email}
                />
                <Grid
                  item
                  xs={12}
                  sm={12}
                  md={12}
                  lg={12}
                  style={{ marginBottom: "20px" }}
                >
                  <FormControl fullWidth>
                    <FormLabel component="legend">{t("payment")}</FormLabel>
                    <Select
                      id="selectedProviderIndex"
                      name="selectedProviderIndex"
                      value={selectedProviderIndex}
                      label={t("payment")}
                      onChange={handleChange}
                      style={{ textAlign: isRTL === "rtl" ? "right" : "left" }}
                      inputProps={{ "aria-label": "Without label" }}
                    >
                      {providers.map((provider, index) => (
                        <MenuItem key={provider.id} value={index}>
                          <img
                            style={{ height: 24, margin: 7 }}
                            src={icons[provider.id]}
                            alt={provider.id}
                          />{" "}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={12} md={12} lg={12}>
                  <Button
                    onClick={handlePaymentModalClose}
                    variant="contained"
                    color="primary"
                  >
                    {t("cancel")}
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    type="submit"
                    style={{ marginLeft: 10 }}
                    onClick={handlePaymentModalClose}
                  >
                    {t("paynow_button")}
                  </Button>
                </Grid>
              </form>
            ) : null}
          </Grid>
        </Modal>
      ) : null}
      <ConfirmationDialogRaw
        open={openConfirm}
        onClose={onConfirmClose}
        value={""}
      />
      {users && data && rowIndex >= 0 ? (
        <Modal
          disablePortal
          disableEnforceFocus
          disableAutoFocus
          onClose={handleClose}
          open={open}
          className={classes.modal}
          container={() => rootRef.current}
        >
          <div className={classes.paper}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography
                  component="h1"
                  variant="h5"
                  className={classes.title}
                  style={{ textAlign: isRTL === "rtl" ? "right" : "left" }}
                >
                  {t("select_driver")}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <UsersCombo
                  className={classes.items}
                  placeholder={t("select_user")}
                  users={users.filter(
                    (usr) => usr.carType === data[rowIndex].carType
                  )}
                  value={userCombo}
                  onChange={(event, newValue) => {
                    setUserCombo(newValue);
                  }}
                />
              </Grid>
              <Grid
                item
                xs={12}
                sm={12}
                md={12}
                lg={12}
                style={{
                  direction: isRTL === "rtl" ? "rtl" : "ltr",
                  marginLeft: isRTL === "rtl" ? "65%" : 0,
                }}
              >
                <Button
                  onClick={handleClose}
                  variant="contained"
                  color="primary"
                >
                  {t("cancel")}
                </Button>
                <Button
                  onClick={assignDriver}
                  variant="contained"
                  color="primary"
                  style={
                    isRTL === "rtl" ? { marginRight: 10 } : { marginLeft: 10 }
                  }
                >
                  {t("assign")}
                </Button>
              </Grid>
            </Grid>
          </div>
        </Modal>
      ) : null}
      <AlertDialog open={commonAlert.open} onClose={handleCommonAlertClose}>
        {commonAlert.msg}
      </AlertDialog>
    </div>
  );
};

export default BookingHistory;
